import type { TranscriptIndex, TranscriptChunk } from './types'
import { logger } from './logger'

const TRASNCRIPT_INDEX_ENDPOINT = '/transcripts/index.json'
const TRASNCRIPT_BASE = '/transcripts/'

let indexCache: TranscriptIndex | null = null
let chunkCache = new Map<string, TranscriptChunk[]>()

export async function getTranscriptIndex(): Promise<TranscriptIndex> {
  if (indexCache) {
    logger.debug('Transcript index cache hit')
    return indexCache
  }
  logger.debug('Fetching transcript index')
  const res = await fetch(TRASNCRIPT_INDEX_ENDPOINT)
  if (!res.ok) throw new Error('Failed to load transcript index')
  const data: TranscriptIndex = await res.json()
  indexCache = data
  logger.info('Transcript index loaded', {
    totalDocuments: data.totalDocuments,
    totalChunks: data.totalChunks,
  })
  return data
}

export async function loadTranscriptChunks(source: string): Promise<TranscriptChunk[]> {
  if (chunkCache.has(source)) return chunkCache.get(source)!
  const filename = source.replace(/\.txt$/, '')
  const res = await fetch(`${TRASNCRIPT_BASE}${filename}.json`)
  if (!res.ok) throw new Error(`Failed to load transcript: ${source}`)
  const chunks: TranscriptChunk[] = await res.json()
  chunkCache.set(source, chunks)
  return chunks
}

function simpleTokenize(text: string): string[] {
  return text.toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(w => w.length > 2)
}

function buildKeywordIndex(chunks: TranscriptChunk[]): Map<string, Set<string>> {
  const index = new Map<string, Set<string>>()
  for (const chunk of chunks) {
    const tokens = simpleTokenize(chunk.preview)
    for (const token of tokens) {
      if (!index.has(token)) index.set(token, new Set())
      index.get(token)!.add(chunk.id)
    }
  }
  return index
}

export async function searchTranscripts(
  query: string,
  maxResults: number = 5
): Promise<{ chunk: TranscriptChunk; score: number }[]> {
  logger.debug('Searching transcripts', { queryPreview: query.slice(0, 60), maxResults })
  const index = await getTranscriptIndex()
  const queryTokens = simpleTokenize(query)

  if (queryTokens.length === 0) {
    logger.debug('Empty query tokens, skipping transcript search')
    return []
  }

  const scores = new Map<string, { chunk: TranscriptChunk; score: number }>()

  for (const chunkMeta of index.chunks) {
    const tokens = simpleTokenize(chunkMeta.preview)
    let score = 0
    for (const qt of queryTokens) {
      for (const t of tokens) {
        if (t.includes(qt) || qt.includes(t)) {
          score += 1
        }
      }
    }
    if (score > 0) {
      const existing = scores.get(chunkMeta.id)
      if (!existing || score > existing.score) {
        scores.set(chunkMeta.id, { chunk: chunkMeta, score })
      }
    }
  }

  const results = Array.from(scores.values())
    .sort((a, b) => b.score - a.score)
    .slice(0, maxResults)

  logger.debug('Transcript search completed', {
    totalScored: scores.size,
    resultsReturned: results.length,
    topScore: results[0]?.score || 0,
  })
  return results
}

export async function getRelevantTranscriptContext(
  query: string,
  maxResults: number = 3
): Promise<string> {
  const results = await searchTranscripts(query, maxResults)

  if (results.length === 0) return ''

  let context = '\n\n[RELEVANT TRANSCRIPTS]:\n\n'
  for (const { chunk, score } of results) {
    context += `--- From: ${chunk.title || chunk.source} (relevance: ${score})\n`
    context += `${chunk.preview}...\n\n`
  }

  return context
}
